import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { api, errorMessage } from '../api';
import { createClientId } from '../format';
import type { CallStatus } from '../types';
import { completeQueuedRecording, enqueueChunk, flushChunks, pendingChunkCount, prepareRecording } from './recordingQueue';
import { connectVoiceCall, endVoiceCall, heartbeatVoiceCall, reserveVoiceCall } from './voiceApi';

interface CallContextValue {
  status: CallStatus;
  muted: boolean;
  speakerMuted: boolean;
  recording: 'off' | 'recording' | 'uploading' | 'incomplete' | 'complete';
  elapsedMs: number;
  error: string;
  start: (options: { conversationId?: string; inviteId?: string }) => Promise<void>;
  end: () => Promise<void>;
  toggleMute: () => void;
  toggleSpeaker: () => void;
  toggleRecording: () => void;
}

const CallContext = createContext<CallContextValue | null>(null);

function supportedMimeType(): string | undefined {
  const candidates = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4'];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type));
}

export function CallProvider({ children }: PropsWithChildren) {
  const [status, setStatus] = useState<CallStatus>('idle');
  const [muted, setMuted] = useState(false);
  const [speakerMuted, setSpeakerMuted] = useState(false);
  const [recordingEnabled, setRecordingEnabled] = useState(true);
  const [recording, setRecording] = useState<CallContextValue['recording']>('off');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState('');
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const callRef = useRef<{ id: string; generation: number; recordingId?: string } | null>(null);
  const startedAtRef = useRef(0);
  const sequenceRef = useRef(0);

  const stopLocalMedia = useCallback(() => {
    if (recorderRef.current?.state !== 'inactive') recorderRef.current?.stop();
    recorderRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    peerRef.current?.close();
    peerRef.current = null;
    if (audioRef.current) {
      audioRef.current.srcObject = null;
      audioRef.current.remove();
      audioRef.current = null;
    }
  }, []);

  const beginRecorder = useCallback((stream: MediaStream, recordingId: string) => {
    if (!recordingEnabled || !('MediaRecorder' in window)) return;
    const mimeType = supportedMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorderRef.current = recorder;
    sequenceRef.current = 0;
    setRecording('recording');
    recorder.addEventListener('dataavailable', (event) => {
      if (!event.data.size) return;
      const sequence = sequenceRef.current++;
      const endMs = performance.now() - startedAtRef.current;
      const startMs = Math.max(0, endMs - 5_000);
      void enqueueChunk({ recordingId, channel: 'owner', sequence, blob: event.data, startMs, endMs })
        .then(() => flushChunks(recordingId))
        .catch(() => setRecording('incomplete'));
    });
    recorder.addEventListener('stop', () => {
      setRecording('uploading');
      void flushChunks(recordingId)
        .then(async () => {
          const count = await pendingChunkCount(recordingId);
          if (count) return setRecording('incomplete');
          await completeQueuedRecording(recordingId, sequenceRef.current);
          setRecording('complete');
        })
        .catch(() => setRecording('incomplete'));
    });
    recorder.start(5_000);
  }, [recordingEnabled]);

  const start = useCallback(async ({ conversationId, inviteId }: { conversationId?: string; inviteId?: string }) => {
    if (!['idle', 'ended', 'failed'].includes(status)) return;
    setError('');
    setStatus('reserved');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true }, video: false });
      streamRef.current = stream;
      setStatus('connecting');
      const peer = new RTCPeerConnection();
      peerRef.current = peer;
      stream.getTracks().forEach((track) => peer.addTrack(track, stream));
      const audio = document.createElement('audio');
      audio.autoplay = true;
      audio.setAttribute('aria-hidden', 'true');
      audio.hidden = true;
      document.body.append(audio);
      audioRef.current = audio;
      peer.addEventListener('track', (event) => { audio.srcObject = event.streams[0] ?? new MediaStream([event.track]); });
      peer.addEventListener('connectionstatechange', () => {
        if (peer.connectionState === 'connected') setStatus('active');
        if (peer.connectionState === 'disconnected') setStatus('reconnecting');
        if (peer.connectionState === 'failed') {
          setStatus('failed');
          setError('فقد الاتصال الصوتي. أوقفنا الصوت محليًا.');
          stopLocalMedia();
        }
      });
      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      let callId: string;
      if (inviteId) {
        callId = (await api.acceptInvite(inviteId)).callId;
      } else {
        if (!conversationId) throw new Error('لا توجد محادثة مرتبطة لبدء المكالمة.');
        callId = (await reserveVoiceCall(conversationId)).id;
      }
      const grant = await connectVoiceCall(callId, {
        operationId: createClientId(),
        sdpOffer: offer.sdp ?? ''
      });
      callRef.current = { id: grant.callId, generation: grant.generation };
      await peer.setRemoteDescription({ type: 'answer', sdp: grant.sdpAnswer });
      startedAtRef.current = performance.now();
      setElapsedMs(0);
      setStatus('active');
      if (recordingEnabled) {
        try {
          const recordingId = await prepareRecording(grant.callId);
          callRef.current = { id: grant.callId, generation: grant.generation, recordingId };
          beginRecorder(stream, recordingId);
        } catch {
          setRecording('incomplete');
        }
      }
    } catch (caught) {
      stopLocalMedia();
      setStatus('failed');
      setError(errorMessage(caught));
    }
  }, [beginRecorder, recordingEnabled, status, stopLocalMedia]);

  const end = useCallback(async () => {
    const call = callRef.current;
    setStatus('ending');
    stopLocalMedia();
    callRef.current = null;
    if (!call) { setStatus('ended'); return; }
    try {
      await endVoiceCall(call.id, call.generation);
      setStatus('ended');
    } catch (caught) {
      setStatus('failed');
      setError(`توقف الصوت محليًا، لكن تعذر تأكيد الإغلاق: ${errorMessage(caught)}`);
    }
  }, [stopLocalMedia]);

  useEffect(() => {
    if (status !== 'active' && status !== 'reconnecting') return;
    const timer = window.setInterval(() => setElapsedMs(performance.now() - startedAtRef.current), 1_000);
    return () => window.clearInterval(timer);
  }, [status]);
  useEffect(() => {
    if (status !== 'active' && status !== 'reconnecting') return;
    const sendHeartbeat = () => {
      const call = callRef.current;
      if (!call) return;
      void heartbeatVoiceCall(call.id, call.generation).catch(() => setStatus('reconnecting'));
    };
    sendHeartbeat();
    const timer = window.setInterval(sendHeartbeat, 10_000);
    return () => window.clearInterval(timer);
  }, [status]);
  useEffect(() => () => stopLocalMedia(), [stopLocalMedia]);
  useEffect(() => { if (audioRef.current) audioRef.current.muted = speakerMuted; }, [speakerMuted]);

  const value = useMemo<CallContextValue>(() => ({
    status, muted, speakerMuted, recording, elapsedMs, error, start, end,
    toggleMute: () => {
      const next = !muted;
      streamRef.current?.getAudioTracks().forEach((track) => { track.enabled = !next; });
      if (recorderRef.current?.state === 'recording' && next) recorderRef.current.pause();
      if (recorderRef.current?.state === 'paused' && !next) recorderRef.current.resume();
      setMuted(next);
    },
    toggleSpeaker: () => setSpeakerMuted((value) => !value),
    toggleRecording: () => {
      if (!callRef.current) { setRecordingEnabled((value) => !value); return; }
      if (recorderRef.current?.state === 'recording') { recorderRef.current.stop(); setRecordingEnabled(false); }
    }
  }), [elapsedMs, end, error, muted, recording, speakerMuted, start, status]);
  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export function useCall(): CallContextValue {
  const context = useContext(CallContext);
  if (!context) throw new Error('useCall must be used inside CallProvider');
  return context;
}
